import { useMemo, useState } from 'react';
import type { RuntimeAction } from '../../scheduler/actions';

export interface MonitorEntry {
  readonly seq: number;
  readonly action: RuntimeAction;
}

interface MonitorProps {
  readonly entries: readonly MonitorEntry[];
  readonly onClear: () => void;
}

function actionLabel(action: RuntimeAction): string {
  switch (action.kind) {
    case 'trigger':
      return `trigger:${action.eventKind}`;
    case 'release':
      return `release:${action.eventKind}`;
    case 'control':
      return 'control';
    case 'transportStart':
      return 'transport.start';
    case 'transportStop':
      return 'transport.stop';
    case 'panic':
      return 'panic';
  }
}

function payloadOf(action: RuntimeAction): unknown {
  if (action.kind === 'trigger') return action.payload;
  if (action.kind === 'control') return { controlId: action.controlId, value: action.value };
  return {};
}

export function Monitor(props: MonitorProps): JSX.Element {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    if (!filter.trim()) return props.entries;
    const needle = filter.toLowerCase();
    return props.entries.filter((entry) => {
      const label = actionLabel(entry.action);
      const trackId = 'trackId' in entry.action ? entry.action.trackId : '';
      const role = 'role' in entry.action ? entry.action.role : '';
      return `${label} ${trackId} ${role}`.toLowerCase().includes(needle);
    });
  }, [props.entries, filter]);

  function exportJson(): void {
    const data = JSON.stringify(filtered.map((entry) => ({ seq: entry.seq, ...entry.action })), null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'osal-event-monitor.json';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="event-monitor">
      <div className="event-monitor-toolbar">
        <input
          type="text"
          placeholder="Filtern (Aktion, Spur, Rolle)"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="Eventmonitor filtern"
        />
        <button type="button" onClick={props.onClear}>
          Leeren
        </button>
        <button type="button" onClick={exportJson}>
          JSON exportieren
        </button>
      </div>
      <div className="event-monitor-scroll">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Sequenzzeit</th>
              <th>Schedulerzeit</th>
              <th>Pattern</th>
              <th>Track</th>
              <th>Step</th>
              <th>Rolle</th>
              <th>Aktion</th>
              <th>Payload</th>
              <th>Voice-ID</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry) => {
              const action = entry.action;
              const hasVoice = 'voiceId' in action;
              return (
                <tr key={entry.seq}>
                  <td>{entry.seq}</td>
                  <td>{'sequenceTime' in action ? action.sequenceTime.toFixed(3) : '-'}</td>
                  <td>{action.time.toFixed(3)}</td>
                  <td>{'patternId' in action ? action.patternId : '-'}</td>
                  <td>{'trackId' in action ? action.trackId : '-'}</td>
                  <td>{'step' in action ? action.step + 1 : '-'}</td>
                  <td>{'role' in action ? action.role : '-'}</td>
                  <td>{actionLabel(action)}</td>
                  <td className="monitor-payload">{JSON.stringify(payloadOf(action))}</td>
                  <td>{hasVoice ? action.voiceId : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
